// ─── FACTOR BACKTEST (Phase 3) ────────────────────────────────────────────────
// Read-only educational display of historical factor performance following each
// active Signal Board signal type, using Ken French monthly factor data.
// No recommendations, no forward performance claims — historical patterns only.

import { Component, useState, useEffect } from "react";
import { C, s } from "./core";
import { getFactors, getFactorPerformanceWhenSignalFired } from "./frenchFactors";

// This board's signal ids → canonical factor type (display-only bridge, matches
// the SignalBoard Phase 1B map).
const SIGNAL_ID_TO_TYPE = {
  "vix-skew-div":         "vix-skew-divergence",
  "yield-inverted":       "yield-curve-inversion",
  "yield-flat":           "yield-curve-inversion",
  "mv-gap-inflationary":  "monetary-regime",
  "fiscal-dominance":     "fiscal-dominance",
  "unusual-options-flow": "unusual-options",
  "congress-buy-cluster": "congress-trade",
  "tanker-dark-cluster":  "ais-dark",
  "petroleum-move":       "petroleum-draw",
  "news-negative":        "sentiment-extreme",
  "news-positive":        "sentiment-extreme",
};

const FACTOR_DISPLAY = [
  ["MktRF", "Market"],
  ["SMB",   "Small Cap (Size)"],
  ["HML",   "Value"],
  ["Mom",   "Momentum"],
  ["RMW",   "Quality"],
  ["CMA",   "Conservative Inv."],
];

const HORIZONS = [
  ["at1Month",  "1 MONTH"],
  ["at3Month",  "3 MONTHS"],
  ["at6Month",  "6 MONTHS"],
  ["at12Month", "12 MONTHS"],
];

const INTERPRETATION = {
  "vix-skew-divergence":
    "Low volatility and quality stocks historically outperform when this signal fires. Momentum stocks historically underperform.",
  "yield-curve-inversion":
    "Quality and value stocks historically outperform in the 6-18 months following inversion. High beta and momentum stocks historically underperform.",
  "monetary-regime":
    "Hard assets and value historically outperform in inflationary monetary regimes. Long duration bonds and growth stocks historically underperform.",
  "squeeze":
    "Squeeze events are idiosyncratic — factor models have low explanatory power. Stock-specific analysis matters more than factor exposure here.",
  "dark-pool-defended":
    "Institutional accumulation at defended levels historically precedes momentum continuation. Watch for level breaks as the key invalidation.",
  "sentiment-extreme":
    "Sentiment extremes are contrarian signals. Markets historically mean-revert from multi-year sentiment extremes within 1-3 months.",
};
const DEFAULT_INTERP =
  "Review the factor performance table to identify which factors have historically moved with this signal type.";

const YELLOW = "#FFD700";

class PanelBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch() {}
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

function typeOf(sig) {
  return SIGNAL_ID_TO_TYPE[sig.id] || sig.id;
}

// Signal firing history is not persisted yet; look for it on globalSignals so
// this populates automatically once a history store exists. Empty → the perf
// function returns null (insufficient sample) and we show that gracefully.
function deriveHistory(type, globalSignals) {
  const hist = globalSignals?.signalHistory;
  if (Array.isArray(hist)) return hist.filter((e) => e && (!e.signalType || e.signalType === type));
  if (hist && Array.isArray(hist[type])) return hist[type];
  return [];
}

function pctFmt(v) {
  return typeof v === "number" ? `${(v * 100).toFixed(1)}%` : "—";
}

function returnColor(v) {
  if (typeof v !== "number") return C.textDim;
  return v >= 0 ? C.green : C.red;
}

function strongestEdge(perf) {
  let best = null;
  for (const [hKey, hLabel] of HORIZONS) {
    const row = perf[hKey] || {};
    for (const [f, fname] of FACTOR_DISPLAY) {
      const v = row[f];
      if (typeof v !== "number") continue;
      if (!best || Math.abs(v) > Math.abs(best.v)) best = { v, fname, hLabel };
    }
  }
  return best;
}

function earliestDate(history) {
  const dates = history.map((e) => e.firedAt).filter(Boolean).sort();
  if (!dates.length) return "—";
  const d = new Date(dates[0]);
  return Number.isNaN(d.getTime()) ? "—" : d.toISOString().slice(0, 10);
}

function BacktestSection({ sig, globalSignals }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [perf, setPerf] = useState(null);

  const type = typeOf(sig);
  const interp = INTERPRETATION[type] || DEFAULT_INTERP;

  useEffect(() => {
    if (!open || loaded) return undefined;
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        await getFactors().catch(() => []);
        const history = deriveHistory(type, globalSignals);
        const result = await getFactorPerformanceWhenSignalFired(type, history);
        if (!cancelled) {
          setPerf(result);
          setLoaded(true);
          setLoading(false);
        }
      } catch {
        if (!cancelled) {
          setPerf(null);
          setLoaded(true);
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, loaded, type, globalSignals]);

  const history = deriveHistory(type, globalSignals);
  const edge = perf ? strongestEdge(perf) : null;

  return (
    <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 8, marginTop: 8 }}>
      {/* Header row — clickable to expand */}
      <div
        onClick={() => setOpen((o) => !o)}
        style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", userSelect: "none" }}
      >
        <span style={{ fontSize: 11, color: C.amber, fontWeight: 700 }}>{sig.title}</span>
        <span style={{ fontSize: 9, color: C.textDim }}>
          {open ? "▴" : "▾"} HISTORICAL FACTOR PERFORMANCE
        </span>
      </div>

      {open && (
        <div style={{ marginTop: 8 }}>
          {loading && <div style={{ fontSize: 10, color: C.textDim }}>Loading factor history…</div>}

          {!loading && loaded && !perf && (
            <div style={{ fontSize: 10, color: C.textDim }}>
              Insufficient signal history for statistical analysis.
            </div>
          )}

          {!loading && perf && (
            <>
              <table style={{ width: "100%", borderCollapse: "collapse", marginBottom: 6 }}>
                <thead>
                  <tr>
                    {["FACTOR", ...HORIZONS.map(([, l]) => l)].map((h, i) => (
                      <th
                        key={h}
                        style={{
                          fontSize: 9,
                          color: C.textDim,
                          fontWeight: 700,
                          textAlign: i === 0 ? "left" : "right",
                          padding: "2px 6px",
                        }}
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {FACTOR_DISPLAY.map(([f, fname]) => (
                    <tr key={f}>
                      <td style={{ fontSize: 10, color: C.textMid, padding: "2px 6px" }}>{fname}</td>
                      {HORIZONS.map(([hKey]) => {
                        const v = perf[hKey]?.[f];
                        return (
                          <td
                            key={hKey}
                            style={{ fontSize: 10, color: returnColor(v), textAlign: "right", padding: "2px 6px" }}
                          >
                            {pctFmt(v)}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>

              <div style={{ fontSize: 9, color: C.textDim }}>
                Based on {perf.occurrences} signal occurrence{perf.occurrences === 1 ? "" : "s"} since{" "}
                {earliestDate(history)}
              </div>
              {edge && (
                <div style={{ fontSize: 9, color: C.amber, marginTop: 2 }}>
                  Strongest edge: {edge.fname} at {edge.hLabel} ({pctFmt(edge.v)} avg)
                </div>
              )}
            </>
          )}

          {/* Interpretation — hardcoded educational context per signal type */}
          <div style={{ fontSize: 10, color: C.textDim, fontStyle: "italic", marginTop: 6, lineHeight: 1.6 }}>
            {interp}
          </div>
        </div>
      )}
    </div>
  );
}

function FactorBacktestInner({ activeSignals, globalSignals }) {
  if (!activeSignals || activeSignals.length === 0) return null;

  return (
    <div style={s.panel}>
      <div style={s.panelB}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.amber, letterSpacing: "0.15em", marginBottom: 4 }}>
          FACTOR BACKTEST
        </div>

        {activeSignals.map((sig) => (
          <BacktestSection key={sig.id} sig={sig} globalSignals={globalSignals} />
        ))}

        <div style={{ fontSize: 10, fontWeight: 700, color: YELLOW, marginTop: 12, lineHeight: 1.6 }}>
          Historical factor performance does not predict future results. Signal occurrences are matched
          to monthly factor returns — horizons are approximate. For informational purposes
          only. Not a recommendation.
        </div>
      </div>
    </div>
  );
}

export function FactorBacktest(props) {
  return (
    <PanelBoundary>
      <FactorBacktestInner {...props} />
    </PanelBoundary>
  );
}
